import { useEffect, useState } from "react";
import api from "../../lib/api";
import {
  CalendarBlank, Crown, X, MoonStars, Rocket, PencilSimple, Check, SignIn,
  GoogleLogo, MicrosoftOutlookLogo, AppleLogo, WarningCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { startImpersonation } from "../../lib/impersonation";

/**
 * Two small KPI tiles for the admin top row:
 *   - CalendarConnectionsTile: clickable — opens a modal listing every
 *                              owner currently using ANY of the three
 *                              calendar integrations (Google two-way,
 *                              Outlook two-way, or Apple/Outlook iCal
 *                              subscription feed). Includes a kind
 *                              filter so admins can drill into any
 *                              single channel.
 *   - PaidProTile:         count of actively paying Pro subscribers.
 *                          Filter on the backend: stripe_subscription_id
 *                          set AND subscription_status in active/trialing.
 *                          Excludes comp, founder, manual-grant Pros.
 */

function Tile({ label, value, Icon, testId, tone = "default", hint, onClick }) {
  const Comp = onClick ? "button" : "div";
  return (
    <Comp
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={
        "text-left w-full rounded-2xl border bg-card p-4 transition-colors " +
        (tone === "primary"
          ? "border-primary/40 ring-1 ring-primary/10 "
          : "border-border ") +
        (onClick ? "hover:bg-muted/40 cursor-pointer" : "")
      }
      data-testid={testId}
      title={hint}
    >
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
        <Icon weight="duotone" className="h-4 w-4 text-primary" />
      </div>
      <div className="mt-2 font-display font-bold text-2xl tabular-nums">
        {value == null ? "—" : value}
      </div>
    </Comp>
  );
}

export function CalendarConnectionsTile() {
  const [count, setCount] = useState(null);
  const [byKind, setByKind] = useState(null);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [loadingList, setLoadingList] = useState(false);
  const [filter, setFilter] = useState("all"); // 'all' | 'gcal' | 'outlook' | 'ical'
  const [attempts, setAttempts] = useState(null);
  const [loadingAttempts, setLoadingAttempts] = useState(false);
  const [showAttempts, setShowAttempts] = useState(false);

  useEffect(() => {
    let on = true;
    api
      .get("/admin/kpis/calendar-connections")
      .then((r) => {
        if (!on) return;
        setCount(r.data?.count ?? 0);
        setByKind(r.data?.by_kind || null);
      })
      .catch(() => on && setCount(0));
    return () => { on = false; };
  }, []);

  const openModal = async () => {
    setOpen(true);
    if (items != null) return; // cache after first load
    setLoadingList(true);
    try {
      const r = await api.get("/admin/kpis/calendar-connections/list");
      setItems(r.data?.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoadingList(false);
    }
  };

  const toggleAttempts = async () => {
    const next = !showAttempts;
    setShowAttempts(next);
    if (next && attempts == null) {
      setLoadingAttempts(true);
      try {
        const r = await api.get("/admin/kpis/calendar-attempts");
        setAttempts(r.data || null);
      } catch {
        setAttempts({ gcal: {}, outlook: {}, recent_attempts: [] });
      } finally {
        setLoadingAttempts(false);
      }
    }
  };

  const filteredItems = (items || []).filter((it) => {
    if (filter === "all") return true;
    return (it.kinds || []).includes(filter);
  });

  return (
    <>
      <Tile
        label="Calendar synced"
        value={count}
        Icon={CalendarBlank}
        testId="admin-kpi-calendar-connections"
        hint="Owners using any of the 3 calendar integrations (Google, Outlook, Apple feed). Click for the trifecta breakdown."
        onClick={openModal}
      />
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setOpen(false)}
          data-testid="calendar-connections-modal"
        >
          <div
            className="w-full max-w-3xl rounded-2xl border border-border bg-card shadow-xl max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="flex items-center justify-between gap-3 px-5 py-4 border-b border-border">
              <div className="flex items-center gap-2 flex-wrap">
                <CalendarBlank weight="duotone" className="h-5 w-5 text-primary" />
                <h2 className="font-display font-semibold text-lg tracking-tight">
                  Calendar sync — connected owners
                </h2>
                {count != null && (
                  <span className="text-[10px] uppercase tracking-wider rounded-full bg-muted px-2 py-0.5 font-bold text-muted-foreground">
                    {count} unique
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-full p-1 hover:bg-muted/60"
                data-testid="calendar-connections-modal-close"
                aria-label="Close"
              >
                <X weight="bold" className="h-4 w-4" />
              </button>
            </header>

            {/* Trifecta breakdown bar */}
            {byKind && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 px-5 pt-4">
                <KindStat
                  active={filter === "all"}
                  onClick={() => setFilter("all")}
                  label="All"
                  value={count ?? 0}
                  testId="cal-kind-all"
                />
                <KindStat
                  active={filter === "gcal"}
                  onClick={() => setFilter("gcal")}
                  label="Google"
                  value={byKind.gcal || 0}
                  Icon={GoogleLogo}
                  testId="cal-kind-gcal"
                />
                <KindStat
                  active={filter === "outlook"}
                  onClick={() => setFilter("outlook")}
                  label="Outlook"
                  value={byKind.outlook || 0}
                  Icon={MicrosoftOutlookLogo}
                  testId="cal-kind-outlook"
                />
                <KindStat
                  active={filter === "ical"}
                  onClick={() => setFilter("ical")}
                  label="Apple / iCal"
                  value={byKind.ical || 0}
                  Icon={AppleLogo}
                  testId="cal-kind-ical"
                />
              </div>
            )}
            {byKind && byKind.multi > 0 && (
              <div className="px-5 pt-2 text-[11px] text-muted-foreground">
                {byKind.multi} owner{byKind.multi === 1 ? "" : "s"} use more than one calendar integration.
              </div>
            )}

            {/* Attempts & failures expandable — answers "who tried but
                didn't finish?" which the success-only list above hides. */}
            <div className="px-5 pt-3">
              <button
                type="button"
                onClick={toggleAttempts}
                className="w-full flex items-center justify-between gap-2 rounded-lg border border-border bg-card hover:bg-muted/40 px-3 py-2 text-[12px] font-semibold text-foreground transition"
                data-testid="calendar-attempts-toggle"
              >
                <span className="flex items-center gap-2">
                  <WarningCircle weight="duotone" className="h-4 w-4 text-amber-500" />
                  Attempts &amp; failures (started but didn&apos;t finish, revoked, errored)
                </span>
                <span className="text-[11px] text-muted-foreground">
                  {showAttempts ? "Hide" : "Show"}
                </span>
              </button>
              {showAttempts && (
                <div className="mt-2 rounded-lg border border-amber-500/20 bg-amber-500/[0.04] p-3" data-testid="calendar-attempts-panel">
                  {loadingAttempts ? (
                    <div className="text-[12px] text-muted-foreground">Loading attempts…</div>
                  ) : !attempts ? (
                    <div className="text-[12px] text-muted-foreground">Couldn&apos;t load.</div>
                  ) : (
                    <>
                      <div className="flex justify-end mb-2">
                        <CleanupStaleStatesButton onCleaned={async () => {
                          // Re-fetch the attempts panel after cleanup so
                          // the now-removed rows disappear instantly.
                          try {
                            const r = await api.get("/admin/kpis/calendar-attempts");
                            setAttempts(r.data || null);
                          } catch (_ignored) { /* refresh failed — panel stays as-is */ }
                        }} />
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-[12px]">
                        <AttemptColumn provider="Google" data={attempts.gcal} />
                        <AttemptColumn provider="Outlook" data={attempts.outlook} />
                      </div>
                      {attempts.recent_attempts && attempts.recent_attempts.length > 0 ? (
                        <div className="mt-3">
                          <div className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground mb-1.5">
                            Recent stuck / abandoned ({attempts.recent_attempts.length})
                          </div>
                          <ul className="divide-y divide-amber-500/15 max-h-60 overflow-y-auto">
                            {attempts.recent_attempts.map((r, idx) => (
                              <li
                                key={`${r.provider}-${r.owner_id}-${idx}`}
                                className="py-2 text-[12px]"
                                data-testid={`calendar-attempt-row-${r.provider}-${r.owner_id}`}
                              >
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-semibold">{r.truck_name || r.name || "(no name)"}</span>
                                  {r.is_pro && (
                                    <span className="inline-flex items-center gap-0.5 text-[10px] uppercase tracking-wider rounded-full bg-primary/10 text-primary px-1.5 py-0.5 font-bold">
                                      <Crown weight="fill" className="h-2.5 w-2.5" />
                                      Pro
                                    </span>
                                  )}
                                  <KindBadge kind={r.provider === "Google" ? "gcal" : r.provider} />
                                  <StageBadge stage={r.stage} />
                                </div>
                                <div className="text-muted-foreground truncate">
                                  {r.email || "(no email)"}
                                  {r.started_at && (
                                    <> · {new Date(r.started_at).toLocaleString()}</>
                                  )}
                                </div>
                                {r.error && (
                                  <div className="text-rose-500 truncate text-[11px] mt-0.5">
                                    {r.error}
                                  </div>
                                )}
                                {r.stage === "abandoned" && r.email && (
                                  <RecoveryNudgeButton
                                    ownerId={r.owner_id}
                                    provider={r.provider === "Google" ? "gcal" : "outlook"}
                                    alreadyNudgedAt={r.recovery_nudged_at}
                                  />
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : (
                        <div className="mt-3 text-[12px] text-muted-foreground">
                          No stuck or abandoned attempts. Every started flow either completed or is fresh enough to still be in-flight.
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4">
              {loadingList ? (
                <div className="text-sm text-muted-foreground">Loading…</div>
              ) : !items || items.length === 0 ? (
                <div className="rounded-xl border border-dashed border-border p-6 text-center">
                  <CalendarBlank weight="duotone" className="h-8 w-8 mx-auto text-muted-foreground" />
                  <p className="mt-2 text-sm text-muted-foreground">
                    No owners have connected any calendar yet.
                  </p>
                </div>
              ) : filteredItems.length === 0 ? (
                <div className="rounded-xl border border-dashed border-border p-6 text-center">
                  <p className="text-sm text-muted-foreground">
                    No owners in this filter.
                  </p>
                </div>
              ) : (
                <ul className="divide-y divide-border">
                  {filteredItems.map((it) => (
                    <ConnectionRow key={it.owner_id} it={it} />
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function KindStat({ label, value, Icon, active, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left rounded-xl border px-3 py-2.5 transition ${
        active
          ? "border-primary/60 bg-primary/5 ring-1 ring-primary/20"
          : "border-border bg-card hover:bg-muted/40"
      }`}
      data-testid={testId}
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
        {Icon && <Icon weight="duotone" className="h-3.5 w-3.5 text-primary" />}
        {label}
      </div>
      <div className="mt-1 font-display font-bold text-xl tabular-nums">{value}</div>
    </button>
  );
}

function AttemptColumn({ provider, data }) {
  const rows = [
    { label: "Currently mid-flow", value: data?.in_flight ?? 0 },
    { label: "Abandoned (>15min)", value: data?.abandoned ?? 0, alert: (data?.abandoned ?? 0) > 0 },
    { label: "Token revoked", value: data?.revoked ?? 0, alert: (data?.revoked ?? 0) > 0 },
    { label: "Last sync errored", value: data?.errored ?? 0, alert: (data?.errored ?? 0) > 0 },
    { label: "No calendar picked", value: data?.pending_cal ?? 0, alert: (data?.pending_cal ?? 0) > 0 },
    { label: "Never synced once", value: data?.never_synced ?? 0 },
  ];
  return (
    <div className="rounded-lg border border-border bg-card p-2.5">
      <div className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground mb-1.5">
        {provider}
      </div>
      <ul className="space-y-0.5">
        {rows.map((r) => (
          <li
            key={r.label}
            className={`flex items-center justify-between gap-2 text-[11.5px] ${r.alert ? "text-amber-700 dark:text-amber-300 font-semibold" : "text-muted-foreground"}`}
          >
            <span>{r.label}</span>
            <span className="tabular-nums">{r.value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function StageBadge({ stage }) {
  const cfg = {
    abandoned: { label: "Abandoned", color: "bg-amber-500/15 text-amber-700 dark:text-amber-300" },
    revoked: { label: "Token revoked", color: "bg-rose-500/15 text-rose-700 dark:text-rose-300" },
    error: { label: "Errored", color: "bg-rose-500/15 text-rose-700 dark:text-rose-300" },
    pending_cal: { label: "No calendar picked", color: "bg-orange-500/15 text-orange-700 dark:text-orange-300" },
  }[stage] || { label: stage, color: "bg-muted text-muted-foreground" };
  return (
    <span className={`inline-flex items-center text-[10px] uppercase tracking-wider rounded-full px-1.5 py-0.5 font-bold ${cfg.color}`}>
      {cfg.label}
    </span>
  );
}


/**
 * RecoveryNudgeButton — one-click "Send recovery email" for an abandoned
 * OAuth attempt. After firing, swaps to a "Nudged · MM/DD" pill with the
 * cooldown locked (server enforces 14-day cooldown). Pre-nudged rows
 * render the locked pill directly via `alreadyNudgedAt`.
 */
function RecoveryNudgeButton({ ownerId, provider, alreadyNudgedAt }) {
  const [sending, setSending] = useState(false);
  const [nudgedAt, setNudgedAt] = useState(alreadyNudgedAt || null);

  const send = async () => {
    if (sending || nudgedAt) return;
    if (!window.confirm("Send a 'finish your calendar setup' email to this owner?")) return;
    setSending(true);
    try {
      const r = await api.post("/admin/calendar/recovery-nudge", {
        owner_id: ownerId,
        provider,
      });
      const ts = r?.data?.nudged_at || new Date().toISOString();
      setNudgedAt(ts);
      toast.success(`Recovery email sent to ${r?.data?.email || "owner"}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Send failed";
      if (err?.response?.status === 409) {
        toast.info(msg);
      } else {
        toast.error(msg);
      }
    } finally {
      setSending(false);
    }
  };

  if (nudgedAt) {
    return (
      <div
        className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 px-2 py-0.5"
        data-testid={`calendar-recovery-nudged-${provider}-${ownerId}`}
      >
        <Check weight="bold" className="h-2.5 w-2.5" />
        Nudged · {new Date(nudgedAt).toLocaleDateString()}
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={send}
      disabled={sending}
      data-testid={`calendar-recovery-send-${provider}-${ownerId}`}
      className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider rounded-full bg-primary/10 hover:bg-primary/20 text-primary px-2 py-0.5 transition-colors disabled:opacity-50"
    >
      {sending ? "Sending…" : "→ Send recovery email"}
    </button>
  );
}


/**
 * CleanupStaleStatesButton — one-shot maintenance: delete every OAuth
 * state row whose owner has already completed a connection, AND dedupe
 * to the most-recent state per (owner, provider). Production sees
 * dozens of these junk rows from owners who retried the OAuth flow
 * several times before getting it right.
 */
function CleanupStaleStatesButton({ onCleaned }) {
  const [running, setRunning] = useState(false);
  const run = async () => {
    if (running) return;
    if (!window.confirm(
      "Delete all OAuth state rows for owners who already finished connecting? " +
      "Also deduplicates to the most-recent state per owner. This is a one-shot " +
      "cleanup — safe to re-run anytime."
    )) return;
    setRunning(true);
    try {
      const r = await api.post("/admin/calendar/cleanup-stale-oauth-states", {});
      const g = r?.data?.gcal_deleted ?? 0;
      const o = r?.data?.outlook_deleted ?? 0;
      toast.success(`Cleaned ${g + o} stale rows (Google ${g} · Outlook ${o})`);
      if (onCleaned) await onCleaned();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cleanup failed");
    } finally {
      setRunning(false);
    }
  };
  return (
    <button
      type="button"
      onClick={run}
      disabled={running}
      data-testid="calendar-cleanup-stale-states"
      className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider rounded-full border border-border bg-background hover:bg-muted/40 px-2 py-1 transition-colors disabled:opacity-50"
    >
      {running ? "Cleaning…" : "🧹 Clean up stale rows"}
    </button>
  );
}


function KindBadge({ kind }) {
  const cfg = {
    gcal: { label: "Google", Icon: GoogleLogo, color: "bg-blue-500/15 text-blue-700 dark:text-blue-300" },
    outlook: { label: "Outlook", Icon: MicrosoftOutlookLogo, color: "bg-sky-500/15 text-sky-700 dark:text-sky-300" },
    ical: { label: "Apple / iCal", Icon: AppleLogo, color: "bg-slate-500/15 text-slate-700 dark:text-slate-300" },
  }[kind];
  if (!cfg) return null;
  const { Icon } = cfg;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider rounded-full px-1.5 py-0.5 font-bold ${cfg.color}`}
      data-testid={`calendar-kind-badge-${kind}`}
    >
      <Icon weight="fill" className="h-2.5 w-2.5" />
      {cfg.label}
    </span>
  );
}

function ConnectionRow({ it }) {
  const lastSync =
    it.gcal?.last_sync_at || it.outlook?.last_sync_at || null;
  return (
    <li
      className="py-3 flex items-start gap-3"
      data-testid={`calendar-connection-row-${it.owner_id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-sm text-foreground truncate">
            {it.truck_name || it.name || "(no truck name)"}
          </span>
          {it.is_pro && (
            <span className="inline-flex items-center gap-0.5 text-[10px] uppercase tracking-wider rounded-full bg-primary/10 text-primary px-1.5 py-0.5 font-bold">
              <Crown weight="fill" className="h-2.5 w-2.5" />
              Pro
            </span>
          )}
          {(it.kinds || []).map((k) => (
            <KindBadge key={k} kind={k} />
          ))}
        </div>
        <div className="mt-0.5 text-[12px] text-muted-foreground truncate">
          {it.email || "(no email)"}
          {it.name && it.truck_name && ` · ${it.name}`}
        </div>
        {it.gcal && (
          <div className="mt-1 text-[11px] text-muted-foreground truncate">
            <GoogleLogo weight="fill" className="inline-block h-3 w-3 mr-1 align-text-bottom" />
            {it.gcal.calendar_summary || it.gcal.calendar_id || "—"}
            {it.gcal.google_email && it.gcal.google_email !== it.email && (
              <> · <span className="text-foreground">{it.gcal.google_email}</span></>
            )}
            {it.gcal.last_sync_status && it.gcal.last_sync_status !== "ok" && (
              <span className="ml-1 text-rose-500 font-semibold">· {it.gcal.last_sync_status}</span>
            )}
          </div>
        )}
        {it.outlook && (
          <div className="mt-1 text-[11px] text-muted-foreground truncate">
            <MicrosoftOutlookLogo weight="fill" className="inline-block h-3 w-3 mr-1 align-text-bottom" />
            {it.outlook.calendar_summary || it.outlook.calendar_id || "—"}
            {it.outlook.outlook_email && it.outlook.outlook_email !== it.email && (
              <> · <span className="text-foreground">{it.outlook.outlook_email}</span></>
            )}
            {it.outlook.last_sync_status && it.outlook.last_sync_status !== "ok" && (
              <span className="ml-1 text-rose-500 font-semibold">· {it.outlook.last_sync_status}</span>
            )}
          </div>
        )}
        {it.ical && (
          <div className="mt-1 text-[11px] text-muted-foreground truncate">
            <AppleLogo weight="fill" className="inline-block h-3 w-3 mr-1 align-text-bottom" />
            Subscribe feed generated
            {it.ical.created_at && (
              <> · {new Date(it.ical.created_at).toLocaleDateString()}</>
            )}
          </div>
        )}
        {lastSync && (
          <div className="mt-0.5 text-[11px] text-muted-foreground">
            Last sync: {new Date(lastSync).toLocaleString()}
          </div>
        )}
      </div>
    </li>
  );
}

// Legacy alias — keep so any external import of `GCalConnectionsTile`
// still resolves to the new trifecta tile without a code change.
export const GCalConnectionsTile = CalendarConnectionsTile;

export function NeverPostedTile() {
  const [count, setCount] = useState(null);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [loadingList, setLoadingList] = useState(false);
  const [kickstartPreview, setKickstartPreview] = useState(null); // { count, items }
  const [kickstartBusy, setKickstartBusy] = useState(false);
  const [filter, setFilter] = useState("all"); // 'all' | 'cold' | 'hot'
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [savingId, setSavingId] = useState(null);
  const [impersonatingId, setImpersonatingId] = useState(null);
  // "Open as owner" scope (Mar 2026): admin asked to open ANY truck —
  // not just never-posted ones — to help with schedule requests without
  // the image-extractor detour. Defaults to All trucks.
  const [scope, setScope] = useState("all"); // 'all' | 'never'
  const [allItems, setAllItems] = useState(null);
  const [search, setSearch] = useState("");

  // Refresh the headline count — used on first mount AND every time the
  // modal closes. Cheap call (single aggregation), keeps the tile in
  // sync after admin actions like Kickstart or an owner finally posting.
  const refreshCount = async () => {
    try {
      const r = await api.get("/admin/kpis/never-posted");
      setCount(r.data?.count ?? 0);
    } catch { /* leave previous value */ }
  };

  useEffect(() => {
    let on = true;
    api
      .get("/admin/kpis/never-posted")
      .then((r) => on && setCount(r.data?.count ?? 0))
      .catch(() => on && setCount(0));
    return () => { on = false; };
  }, []);

  // Admin impersonation — opens the app AS the target owner so the
  // admin can walk them through schedule setup, address entry, etc.
  // All actions during the session are audited server-side.
  const openAsOwner = async (it) => {
    const label = it.truck_name || it.name || it.email || "this owner";
    if (!window.confirm(
      `Open the app as ${label}?\n\n` +
      `You'll be logged in as them for 60 minutes. A red banner will show at the top — click "Return to admin" any time to come back. Every action is audit-logged.`
    )) return;
    setImpersonatingId(it.owner_id);
    try {
      await startImpersonation(it.owner_id);
      // Hard reload so EVERY cached request + auth provider + page
      // remounts under the target's session.
      window.location.href = "/dashboard";
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Could not start impersonation");
      setImpersonatingId(null);
    }
  };

  // Inline truck-name edit — admin sets the missing truck_name on the
  // owner's behalf so they can begin filling out a schedule. Updates the
  // local list optimistically (so the row shows the new name and the
  // "(no truck name)" placeholder vanishes) without re-hitting the API.
  const saveTruckName = async (ownerId) => {
    const name = (editValue || "").trim();
    if (!name) return;
    setSavingId(ownerId);
    try {
      await api.put(`/admin/users/${ownerId}/truck-name`, { truck_name: name });
      setItems((prev) =>
        (prev || []).map((it) => (it.owner_id === ownerId ? { ...it, truck_name: name } : it))
      );
      toast.success(`Truck name saved — ${name}`);
      setEditingId(null);
      setEditValue("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save truck name");
    } finally {
      setSavingId(null);
    }
  };

  useEffect(() => {
    let on = true;
    api
      .get("/admin/kpis/never-posted")
      .then((r) => on && setCount(r.data?.count ?? 0))
      .catch(() => on && setCount(0));
    return () => { on = false; };
  }, []);

  const openModal = async () => {
    setOpen(true);
    // Re-pull fresh data — list, kickstart preview, AND the headline
    // count. Forces a full refresh every time the modal opens (cheap, and
    // it's the single best moment to spend the read budget).
    setLoadingList(true);
    try {
      const [list, kickPrev, freshCount, allList] = await Promise.all([
        api.get("/admin/kpis/never-posted/list"),
        api.get("/admin/activation/kickstart/preview"),
        api.get("/admin/kpis/never-posted"),
        api.get("/admin/kpis/owners/all"),
      ]);
      setItems(list.data?.items || []);
      setKickstartPreview(kickPrev.data || { count: 0, items: [] });
      setCount(freshCount.data?.count ?? count);
      setAllItems(allList.data?.items || []);
    } catch {
      if (items == null) setItems([]);
      if (allItems == null) setAllItems([]);
      if (kickstartPreview == null) setKickstartPreview({ count: 0, items: [] });
    } finally {
      setLoadingList(false);
    }
  };

  const closeModal = async () => {
    setOpen(false);
    // Lightweight refresh on close — keeps the tile count honest after
    // any in-modal actions (kickstart, truck name edits, etc).
    await refreshCount();
  };

  const runKickstart = async () => {
    const n = kickstartPreview?.count || 0;
    if (n === 0) return;
    if (!window.confirm(
      `Send the activation FOMO email to ${n} owner${n === 1 ? "" : "s"} who are 14+ days past signup (cold) and never received any activation email?\n\n` +
      `This is a one-shot — already-sent owners are skipped. Pro owners get the Pro copy. Continue?`
    )) return;
    setKickstartBusy(true);
    try {
      const r = await api.post("/admin/activation/kickstart");
      toast.success(r.data?.message || "Kickstart batch started.");
      // Optimistic: zero out the preview count so the button hides.
      setKickstartPreview((p) => ({ count: 0, items: p?.items || [] }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kickstart failed to start.");
    } finally {
      setKickstartBusy(false);
    }
  };

  return (
    <>
      <Tile
        label="Never posted"
        value={count}
        Icon={MoonStars}
        testId="admin-kpi-never-posted"
        hint="Owners who have never created a schedule or one-off event. Click to open ANY truck as its owner (All trucks tab) or work the never-posted activation list."
        onClick={openModal}
      />
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={closeModal}
          data-testid="never-posted-modal"
        >
          <div
            className="w-full max-w-2xl rounded-2xl border border-border bg-card shadow-xl max-h-[80vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="flex items-center justify-between gap-3 px-5 py-4 border-b border-border">
              <div className="flex items-center gap-2">
                <MoonStars weight="duotone" className="h-5 w-5 text-primary" />
                <h2 className="font-display font-semibold text-lg tracking-tight">
                  {scope === "all" ? "Trucks — open as owner" : "Owners — never posted an event"}
                </h2>
                {count != null && (
                  <span className="text-[10px] uppercase tracking-wider rounded-full bg-muted px-2 py-0.5 font-bold text-muted-foreground">
                    {count}
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={closeModal}
                className="rounded-full p-1 hover:bg-muted/60"
                data-testid="never-posted-modal-close"
                aria-label="Close"
              >
                <X weight="bold" className="h-4 w-4" />
              </button>
            </header>
            <div className="flex-1 overflow-y-auto px-5 py-4">
              {/* Scope switch (Mar 2026) — "All trucks" is the admin's
                  daily schedule-help workflow; "Never posted" keeps the
                  activation/kickstart tooling. */}
              <div
                className="mb-3 inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 p-0.5"
                data-testid="open-as-owner-scope-tabs"
              >
                {[
                  { id: "all", label: `All trucks${allItems ? ` · ${allItems.length}` : ""}` },
                  { id: "never", label: `Never posted${items ? ` · ${items.length}` : ""}` },
                ].map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setScope(t.id)}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition ${
                      scope === t.id
                        ? "bg-background text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                    data-testid={`open-as-owner-scope-${t.id}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              {/* Kickstart action — only when there are owners past D30
                  who never received the regular series. One-click sends
                  the FOMO email + marks them as "series completed" so
                  they never get re-touched. */}
              {scope === "never" && kickstartPreview && kickstartPreview.count > 0 && (
                <div
                  className="mb-4 rounded-xl border border-orange-400/40 bg-orange-50/60 dark:bg-orange-500/10 px-4 py-3 flex items-start gap-3"
                  data-testid="never-posted-kickstart-banner"
                >
                  <Rocket weight="duotone" className="h-5 w-5 text-orange-600 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-foreground">
                      Backlog kickstart — {kickstartPreview.count} owner{kickstartPreview.count === 1 ? "" : "s"} eligible
                    </div>
                    <div className="text-[12px] text-muted-foreground mt-0.5 leading-relaxed">
                      These owners are 14+ days past signup (the &ldquo;cold&rdquo; threshold), have never posted a stop, AND never received any activation email. One-click sends them the FOMO+urgency template. After this they fall out of the series permanently (unless they post).
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={runKickstart}
                    disabled={kickstartBusy}
                    className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-orange-600 hover:bg-orange-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-[12px] font-semibold px-3 py-1.5"
                    data-testid="never-posted-kickstart-btn"
                  >
                    <Rocket weight="bold" className="h-3.5 w-3.5" />
                    {kickstartBusy ? "Sending…" : "Send kickstart"}
                  </button>
                </div>
              )}

              {loadingList ? (
                <div className="text-sm text-muted-foreground">Loading…</div>
              ) : (
                (() => {
                  const source = scope === "all" ? (allItems || []) : (items || []);
                  if (scope === "never" && source.length === 0) {
                    return (
                      <div className="rounded-xl border border-dashed border-border p-6 text-center">
                        <MoonStars weight="duotone" className="h-8 w-8 mx-auto text-muted-foreground" />
                        <p className="mt-2 text-sm text-muted-foreground">
                          Every owner has posted at least one event. Nice.
                        </p>
                      </div>
                    );
                  }
                  // Filter tabs — keep the visible list aligned with what
                  // the Kickstart action would actually email. Counts are
                  // computed inline so the badge stays in sync.
                  const coldCount = source.filter((x) => (x.days_since_signup ?? 0) >= 14).length;
                  const hotCount = source.length - coldCount;
                  const q = search.trim().toLowerCase();
                  const filtered =
                    scope === "all"
                      ? (q
                          ? source.filter((x) =>
                              [x.truck_name, x.name, x.email].some(
                                (v) => (v || "").toLowerCase().includes(q),
                              ))
                          : source)
                      : filter === "cold"
                      ? source.filter((x) => (x.days_since_signup ?? 0) >= 14)
                      : filter === "hot"
                      ? source.filter((x) => (x.days_since_signup ?? 0) < 14)
                      : source;
                  return (
                    <>
                      {scope === "all" ? (
                        <input
                          type="search"
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          placeholder="Search truck, owner, or email…"
                          className="mb-3 w-full rounded-lg border border-border bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                          data-testid="open-as-owner-search"
                          autoFocus
                        />
                      ) : (
                      <div
                        className="mb-3 inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 p-0.5"
                        data-testid="never-posted-filter-tabs"
                      >
                        {[
                          { id: "all", label: "All", n: source.length },
                          { id: "cold", label: "Cold (14d+)", n: coldCount },
                          { id: "hot", label: "Hot (<14d)", n: hotCount },
                        ].map((t) => (
                          <button
                            key={t.id}
                            type="button"
                            onClick={() => setFilter(t.id)}
                            className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition ${
                              filter === t.id
                                ? "bg-background text-foreground shadow-sm"
                                : "text-muted-foreground hover:text-foreground"
                            }`}
                            data-testid={`never-posted-filter-${t.id}`}
                          >
                            {t.label} <span className="tabular-nums opacity-80">· {t.n}</span>
                          </button>
                        ))}
                      </div>
                      )}
                      {filtered.length === 0 ? (
                        <div className="rounded-xl border border-dashed border-border p-5 text-center text-[12px] text-muted-foreground">
                          {scope === "all" ? "No trucks match that search." : "No owners in this filter."}
                        </div>
                      ) : (
                <ul className="divide-y divide-border">
                  {filtered.map((it) => (
                    <li
                      key={it.owner_id}
                      className="py-3 flex items-start gap-3"
                      data-testid={`never-posted-row-${it.owner_id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          {editingId === it.owner_id ? (
                            <div className="flex items-center gap-1 flex-1 min-w-0">
                              <input
                                type="text"
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") saveTruckName(it.owner_id);
                                  if (e.key === "Escape") { setEditingId(null); setEditValue(""); }
                                }}
                                placeholder="Enter truck name…"
                                autoFocus
                                className="flex-1 min-w-0 rounded-md border border-border bg-background px-2 py-1 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-primary/30"
                                data-testid={`never-posted-edit-input-${it.owner_id}`}
                                maxLength={120}
                              />
                              <button
                                type="button"
                                onClick={() => saveTruckName(it.owner_id)}
                                disabled={savingId === it.owner_id || !editValue.trim()}
                                className="rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white px-2 py-1"
                                data-testid={`never-posted-edit-save-${it.owner_id}`}
                                aria-label="Save"
                              >
                                <Check weight="bold" className="h-3.5 w-3.5" />
                              </button>
                              <button
                                type="button"
                                onClick={() => { setEditingId(null); setEditValue(""); }}
                                className="rounded-md bg-muted hover:bg-muted/80 text-foreground px-2 py-1"
                                aria-label="Cancel"
                              >
                                <X weight="bold" className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          ) : (
                            <>
                              <span className="font-semibold text-sm text-foreground truncate">
                                {it.truck_name || (
                                  <span className="italic text-muted-foreground">(no truck name)</span>
                                )}
                              </span>
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingId(it.owner_id);
                                  setEditValue(it.truck_name || "");
                                }}
                                className="rounded-md p-0.5 hover:bg-muted/60 text-muted-foreground hover:text-foreground"
                                data-testid={`never-posted-edit-btn-${it.owner_id}`}
                                title={it.truck_name ? "Rename truck" : "Add truck name"}
                                aria-label={it.truck_name ? "Rename truck" : "Add truck name"}
                              >
                                <PencilSimple weight="bold" className="h-3 w-3" />
                              </button>
                              {it.is_pro && (
                                <span className="inline-flex items-center gap-0.5 text-[10px] uppercase tracking-wider rounded-full bg-primary/10 text-primary px-1.5 py-0.5 font-bold">
                                  <Crown weight="fill" className="h-2.5 w-2.5" />
                                  Pro
                                </span>
                              )}
                              {scope === "all" && it.has_posted && (
                                <span className="text-[10px] uppercase tracking-wider rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 px-1.5 py-0.5 font-bold">
                                  posted
                                </span>
                              )}
                              {scope === "never" && typeof it.days_since_signup === "number" && it.days_since_signup >= 14 && (
                                <span className="text-[10px] uppercase tracking-wider rounded-full bg-rose-500/15 text-rose-700 dark:text-rose-300 px-1.5 py-0.5 font-bold">
                                  {it.days_since_signup}d cold
                                </span>
                              )}
                            </>
                          )}
                        </div>
                        <div className="mt-0.5 text-[12px] text-muted-foreground truncate">
                          {it.email || "(no email)"}
                          {it.name && it.truck_name && ` · ${it.name}`}
                        </div>
                        <div className="mt-1 text-[11px] text-muted-foreground">
                          Signed up: {it.created_at ? new Date(it.created_at).toLocaleString() : "—"}
                          {typeof it.days_since_signup === "number" && (
                            <span className="ml-1 text-foreground">· {it.days_since_signup} days ago</span>
                          )}
                        </div>
                        <div className="mt-0.5 text-[11px] text-muted-foreground">
                          Last login: {it.last_login_at ? new Date(it.last_login_at).toLocaleString() : "—"}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => openAsOwner(it)}
                        disabled={impersonatingId === it.owner_id}
                        className="shrink-0 inline-flex items-center gap-1 rounded-full border border-border bg-card hover:bg-muted/60 disabled:opacity-60 text-foreground text-[11px] font-semibold px-2.5 py-1 self-center"
                        data-testid={`never-posted-impersonate-${it.owner_id}`}
                        title="Log in as this owner to help them set up"
                      >
                        <SignIn weight="bold" className="h-3 w-3" />
                        {impersonatingId === it.owner_id ? "Opening…" : "Open as owner"}
                      </button>
                    </li>
                  ))}
                </ul>
                      )}
                    </>
                  );
                })()
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}


export function PaidProTile() {
  const [count, setCount] = useState(null);
  const [byPlan, setByPlan] = useState(null);
  useEffect(() => {
    let on = true;
    api
      .get("/admin/kpis/paid-pro")
      .then((r) => {
        if (!on) return;
        setCount(r.data?.count ?? 0);
        setByPlan(r.data?.by_plan || null);
      })
      .catch(() => on && setCount(0));
    return () => { on = false; };
  }, []);
  const hint = byPlan
    ? `${byPlan.founding_5 || 0} Founding ($5) · ${byPlan.standard_15 || 0} Standard ($15)${byPlan.unknown ? ` · ${byPlan.unknown} unknown` : ""}. Owners with an active Stripe subscription. Excludes comp + manual-grant Pros.`
    : "Owners with an active Stripe subscription (status = active or trialing). Excludes admin, founder, comp, and manual-grant Pros.";
  return (
    <Tile
      label="Paid Pros"
      value={count}
      Icon={Crown}
      testId="admin-kpi-paid-pro"
      tone="primary"
      hint={hint}
    />
  );
}
